<template>
  <div class="body landing-page">
    <!-- Navigation Bar -->
    <Navbar 
      :showBackButton="true" 
      @back="goBack"
    />

    <div class="landing-page">
      <!-- Header with Auth CTA -->
      <header class="header" style="padding-top: 20px;">
        <!-- Hero Section -->
        <section class="hero">
          <div class="hero-overlay"></div>
          <div class="container">
            <div class="hero-grid">
              <div class="hero-content">
                <div class="hero-badge">
                  <span class="badge-pulse">✨ {{ $t('landing.hero.badge') }}</span>
                </div>

                <h1 class="hero-title">
                  {{ $t('landing.hero.titleLine1.before') }}
                  <span class="gradient-text">{{ $t('landing.hero.titleLine1.highlight') }}</span><br>

                  {{ $t('landing.hero.titleLine2.before') }}
                  <span class="gradient-text">{{ $t('landing.hero.titleLine2.highlight') }}</span>
                </h1>

                <p class="hero-subtitle">
                  {{ $t('landing.hero.subtitle') }}
                </p>

                <div class="hero-buttons">
                  <button class="btn btn-primary btn-large" @click="scrollToServices">
                    {{ $t('landing.hero.buttons.services') }}
                    <span class="icon">→</span>
                  </button>

                  <button class="btn btn-outline btn-large text-white" @click="showVideoModal = true">
                    <span class="icon">▶</span>
                    {{ $t('landing.hero.buttons.demo') }}
                  </button>
                </div>

                <div class="khane-login-share" dir="rtl">
                  <div>
                    <strong>لینک ورود</strong>
                    <p>با اسکن این کد، وارد صفحه ورود می‌شوید و پس از ورود، خانه نرم افزار به‌صورت خودکار انتخاب می‌شود.</p>
                  </div>
                  <a :href="loginQrImageUrl" target="_blank" rel="noopener noreferrer" aria-label="Open login QR image">
                    <img :src="loginQrImageUrl" alt="Login QR code" class="khane-login-qr" />
                  </a>
                  <!-- <a class="khane-login-link" :href="loginUrl">{{ loginUrl }}</a> -->
                  <button type="button" class="btn btn-outline-light" @click="copyLoginUrl">
                     کپی لینک 
                  </button>
                </div>

                <!-- Trust Badges -->
                <div class="trust-badges">
                  <div class="trust-item">
                    <span class="trust-icon">✅</span>
                    <span>{{ $t('landing.hero.trust.security') }}</span>
                  </div>
                  <div class="trust-item">
                    <span class="trust-icon">⚡</span>
                    <span>{{ $t('landing.hero.trust.support') }}</span>
                  </div>
                  <div class="trust-item">
                    <span class="trust-icon">🎯</span>
                    <span>{{ $t('landing.hero.trust.moneyBack') }}</span>
                  </div>
                </div>
              </div>

              <div class="hero-image-wrapper">
                <div class="hero-image">
                  <img
                    src="/dashboard-preview.svg"
                    :alt="$t('landing.hero.imageAlt')"
                    class="dashboard-preview"
                    :class="{ 'animated': isVisible }"
                  >
                </div>

                <!-- Floating CTA Card -->
                <div class="floating-cta-card" v-if="!authStore?.isAuthenticated">
                  <div class="cta-card-content">
                    <h4>{{ $t('landing.hero.floating.title') }}</h4>
                    <p>{{ $t('landing.hero.floating.subtitle') }}</p>

                    <button class="btn btn-primary btn-small" @click="openAuthModal('register')">
                      {{ $t('landing.hero.floating.button') }}
                    </button>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>
      </header>

      <!-- Stats Section -->
      <section class="stats-section">
        <div class="container">
          <div class="stats-grid">
            <div
              class="stat-card"
              v-for="stat in stats"
              :key="stat.id"
              :class="{ 'animate-in': true }"
            >
              <div class="stat-number">{{ stat.number }}</div>
              <div class="stat-label">{{ $t(stat.labelKey) }}</div>
            </div>
          </div>
        </div>
      </section>

      <!-- Banner -->
      <div class="top-banner">
        <div class="container">
          <div class="banner-content">
            <span class="banner-text">✨ {{ $t('landing.banner.text') }}</span>

            <div class="banner-buttons">
              <button class="btn-banner btn-register" @click="openAuthModal('register')">
                <span class="icon">📝</span>
                {{ $t('landing.banner.register') }}
              </button>

              <button class="btn-banner btn-login" @click="openAuthModal('login')">
                <span class="icon">🔑</span>
                {{ $t('landing.banner.login') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- About Section -->
      <section id="about" class="about-section">
        <div class="container">
          <div class="section-header" :class="{'animate-in': true}">
            <h2 class="section-title" style="padding-top: 50px; padding-bottom: 30px;">{{ $t('landing.about.title') }}</h2>
            <p class="section-subtitle">{{ $t('landing.about.subtitle') }}</p>
          </div>

          <div class="timeline">
            <div
              class="timeline-item"
              v-for="(item, index) in timeline"
              :key="index"
              :style="{ animationDelay: index * 200 + 'ms' }"
              :class="{ 'animate-in': true }"
            >
              <div class="timeline-year">{{ item.year }}</div>
              <div class="timeline-content">
                <h3>{{ $t(item.titleKey) }}</h3>
                <p>{{ $t(item.descriptionKey) }}</p>
              </div>
              <div class="timeline-icon">{{ item.icon }}</div>
            </div>
          </div>
        </div>
      </section>

      <!-- Services Section -->
      <section id="services" class="services-section" style="padding-bottom: 20px;">
        <div class="container">
          <div class="section-header" :class="{'animate-in': true}">
            <h2 class="section-title" style="padding-top: 20px;">{{ $t('landing.services.title') }}</h2>
            <p class="section-subtitle">{{ $t('landing.services.subtitle') }}</p>
          </div>

          <div class="services-grid">
            <div
              class="service-card"
              v-for="service in services"
              :key="service.id"
              :style="{ animationDelay: service.id * 100 + 'ms' }"
              :class="{ 'animate-in': true }"
              @mouseenter="activeService = service.id"
              @mouseleave="activeService = null"
            >
              <div class="service-icon" :class="{ 'active': activeService === service.id }">
                {{ service.icon }}
              </div>

              <h3 class="service-title">{{ $t(service.titleKey) }}</h3>
              <p class="service-description">{{ $t(service.descriptionKey) }}</p>

              <ul class="service-features">
                <li v-for="featureKey in service.featureKeys" :key="featureKey">
                  <span class="feature-check">✓</span>
                  {{ $t(featureKey) }}
                </li>
              </ul>

              <div class="service-footer">
                <button class="btn btn-secondary" @click="showServiceDetails(service)">
                  {{ $t('landing.services.more') }}
                </button>
              </div>
            </div>
          </div>

        </div>
      </section>

      <!-- AI Features -->
      <section class="ai-features">
        <div class="container">
          <div class="ai-header" :class="{'animate-in': true}">
            <h2 class="section-title">{{ $t('landing.ai.title') }}</h2>
            <p class="section-subtitle">{{ $t('landing.ai.subtitle') }}</p>
          </div>

          <div class="ai-grid">
            <div
              class="ai-card"
              v-for="ai in aiFeatures"
              :key="ai.id"
              :style="{ animationDelay: ai.id * 100 + 'ms' }"
              :class="{ 'animate-in': true }"
            >
              <div class="ai-icon">{{ ai.icon }}</div>
              <h3>{{ $t(ai.titleKey) }}</h3>
              <p>{{ $t(ai.descriptionKey) }}</p>
            </div>
          </div>

        </div>
      </section>

      <!-- Journey -->
      <section id="journey" class="journey-section">
        <div class="container">
          <div class="section-header" :class="{ 'animate-in': true }" style="padding-top: 15px;">
            <h2 class="section-title">{{ $t('landing.journey.title') }}</h2>
            <p class="section-subtitle">{{ $t('landing.journey.subtitle') }}</p>
          </div>

          <div class="journey-steps">
            <div
              class="step"
              v-for="(step, index) in steps"
              :key="index"
              :class="{ 'animate-in': true }"
              :style="{ animationDelay: index * 150 + 'ms' }"
            >
              <div class="step-number">{{ step.number }}</div>
              <div class="step-content">
                <h3>{{ $t(step.titleKey) }}</h3>
                <p>{{ $t(step.descriptionKey) }}</p>
              </div>

              <div class="step-progress" :style="{ width: ((index + 1) * 20) + '%' }"></div>
            </div>
          </div>

        </div>
      </section>

      <!-- CTA -->
      <section class="cta-section">
        <div class="container">
          <div class="cta-wrapper">
            <div class="cta-content">

              <h2 class="cta-title">{{ $t('landing.cta.title') }}</h2>

              <p class="cta-description">
                {{ $t('landing.cta.description') }}
              </p>

              <div class="cta-features">
                <div class="cta-feature">
                  <span class="cta-feature-icon">✓</span>
                  <span>{{ $t('landing.cta.feature1') }}</span>
                </div>

                <div class="cta-feature">
                  <span class="cta-feature-icon">✓</span>
                  <span>{{ $t('landing.cta.feature2') }}</span>
                </div>

                <div class="cta-feature">
                  <span class="cta-feature-icon">✓</span>
                  <span>{{ $t('landing.cta.feature3') }}</span>
                </div>
              </div>

              <div class="cta-buttons">
                <button class="btn btn-primary btn-large" @click="openAuthModal('register')">
                  <span class="icon">✨</span>
                  {{ $t('landing.banner.register') }}
                </button>

                <button class="btn btn-outline-light btn-large" @click="openAuthModal('login')">
                  <span class="icon">🔑</span>
                  {{ $t('landing.cta.login') }}
                </button>
              </div>

            </div>

            <div class="cta-image">
              <img src="/cta-illustration.svg" :alt="$t('landing.cta.imageAlt')">
            </div>

          </div>
        </div>
      </section>

      <!-- Contact -->
      <section id="contact" class="contact-section">
        <div class="container">
          <div class="contact-wrapper" style="align-items: center; justify-content: center;">

            <div class="contact-info">
              <h2 class="section-title">{{ $t('landing.contact.title') }}</h2>

              <p class="contact-description">
                {{ $t('landing.contact.subtitle') }}
              </p>

              <div class="contact-details">

                <div class="contact-item">
                  <span class="contact-icon">📞</span>
                  <div>
                    <h4>{{ $t('landing.contact.phoneTitle') }}</h4>
                    <p>{{ $t('landing.contact.phone') }}</p>
                  </div>
                </div>

                <div class="contact-item">
                  <span class="contact-icon">✉️</span>
                  <div>
                    <h4>{{ $t('landing.contact.emailTitle') }}</h4>
                    <p>{{ $t('landing.contact.email') }}</p>
                  </div>
                </div>

                <div class="contact-item">
                  <span class="contact-icon">🕒</span>
                  <div>
                    <h4>{{ $t('landing.contact.hoursTitle') }}</h4>
                    <p>{{ $t('landing.contact.hours') }}</p>
                  </div>
                </div>

              </div>

              <div class="social-proof">
                <h4>
                  {{ $t('landing.contact.socialPrefix') }}
                  <span class="highlight-number">۵۰۰+</span>
                  {{ $t('landing.contact.socialSuffix') }}
                </h4>

                <div class="company-logos">
                  <span class="company-placeholder">🏢</span>
                  <span class="company-placeholder">🏭</span>
                  <span class="company-placeholder">🏪</span>
                  <span class="company-placeholder">🏦</span>
                </div>
              </div>

            </div>

            <!-- CONTACT FORM -->
            <div class="contact-form-wrapper">
              <form @submit.prevent="submitForm" class="contact-form">

                <div class="form-group">
                  <input type="text"
                         v-model="form.name"
                         :placeholder="$t('landing.contact.form.name')"
                         required>
                </div>

                <div class="form-group">
                  <input type="tel"
                         v-model="form.phone"
                         :placeholder="$t('landing.contact.form.phone')"
                         required>
                </div>

                <div class="form-group">
                  <input type="email"
                         v-model="form.email"
                         :placeholder="$t('landing.contact.form.email')">
                </div>

                <div class="form-group">
                  <select v-model="form.businessType" required>
                    <option value="">{{ $t('landing.contact.form.type') }}</option>
                    <option value="store">{{ $t('landing.contact.form.store') }}</option>
                    <option value="company">{{ $t('landing.contact.form.company') }}</option>
                    <option value="factory">{{ $t('landing.contact.form.factory') }}</option>
                    <option value="other">{{ $t('landing.contact.form.other') }}</option>
                  </select>
                </div>

                <div class="form-group">
                  <textarea v-model="form.message"
                            :placeholder="$t('landing.contact.form.message')">
                  </textarea>
                </div>

                <button type="submit"
                        class="btn btn-primary btn-block"
                        :disabled="isSubmitting">
                  <span v-if="isSubmitting" class="spinner"></span>
                  {{ isSubmitting ? $t('landing.contact.form.sending') : $t('landing.contact.form.submit') }}
                </button>

              </form>
            </div>

          </div>
        </div>
      </section>

      <!-- Footer -->
      <footer class="footer">
        <div class="container">
          <div class="footer-content">

            <div class="footer-brand">
              <img src="/logo-white.svg" :alt="$t('brand')" class="footer-logo">

              <p class="footer-tagline">{{ $t('landing.footer.tagline') }}</p>

              <div class="footer-newsletter">
                <h4>{{ $t('landing.footer.newsletter.title') }}</h4>

                <div class="newsletter-form">
                  <input type="email" :placeholder="$t('landing.footer.newsletter.placeholder')">
                  <button class="btn-subscribe">{{ $t('landing.footer.newsletter.button') }}</button>
                </div>
              </div>
            </div>

            <div class="footer-links">

              <div class="link-group">
                <h4>{{ $t('landing.footer.quick.title') }}</h4>
                <a href="#about">{{ $t('landing.footer.quick.about') }}</a>
                <a href="#services">{{ $t('landing.footer.quick.services') }}</a>
                <a href="#journey">{{ $t('landing.footer.quick.journey') }}</a>
                <a href="#contact">{{ $t('landing.footer.quick.contact') }}</a>
              </div>

              <div class="link-group">
                <h4>{{ $t('landing.footer.services.title') }}</h4>
                <a href="#">{{ $t('landing.footer.services.store') }}</a>
                <a href="#">{{ $t('landing.footer.services.erp') }}</a>
                <a href="#">{{ $t('landing.footer.services.factory') }}</a>
                <a href="#">{{ $t('landing.footer.services.ai') }}</a>
              </div>

              <div class="link-group">
                <h4>{{ $t('landing.footer.support.title') }}</h4>
                <a href="#">{{ $t('landing.footer.support.help') }}</a>
                <a href="#">{{ $t('landing.footer.support.docs') }}</a>
                <a href="#">{{ $t('landing.footer.support.blog') }}</a>
                <a href="#">{{ $t('landing.footer.support.faq') }}</a>
              </div>

            </div>

            <div class="footer-social">
              <h4>{{ $t('landing.footer.social.title') }}</h4>

              <div class="social-icons">
                <a href="#" class="social-icon" :title="$t('landing.footer.social.instagram')">📱</a>
                <a href="#" class="social-icon" :title="$t('landing.footer.social.telegram')">📷</a>
                <a href="#" class="social-icon" :title="$t('landing.footer.social.linkedin')">📧</a>
                <a href="#" class="social-icon" :title="$t('landing.footer.social.twitter')">💼</a>
              </div>

              <div class="app-badges">
                <a href="#" class="app-badge">
                  <span>📱</span> {{ $t('landing.footer.social.bazar') }}
                </a>

                <a href="#" class="app-badge">
                  <span>🍎</span> {{ $t('landing.footer.social.appstore') }}
                </a>
              </div>

            </div>

          </div>

          <div class="footer-bottom">
            <p>{{ $t('landing.footer.bottom.rights') }}</p>

            <div class="footer-bottom-links">
              <a href="#">{{ $t('landing.footer.bottom.privacy') }}</a>
              <a href="#">{{ $t('landing.footer.bottom.terms') }}</a>
            </div>
          </div>
        </div>
      </footer>

      <!-- Auth Modal -->
      <div class="modal" v-if="showAuthModal" @click.self="closeAuthModal">
        <div class="modal-content modal-auth">
          <button class="modal-close" @click="closeAuthModal">×</button>

          <div class="auth-tabs">
            <button class="auth-tab" :class="{'active': authTab === 'login'}" @click="authTab = 'login'">
              {{ $t('auth.login') }}
            </button>

            <button class="auth-tab" :class="{'active': authTab === 'register'}" @click="authTab = 'register'">
              {{ $t('auth.register') }}
            </button>
          </div>

          <div class="auth-content">

            <!-- Login Form -->
            <form v-if="authTab === 'login'" @submit.prevent="handleLogin" class="auth-form">

              <div class="form-group">
                <input type="email"
                       v-model="loginForm.email"
                       :placeholder="$t('auth.email')"
                       required>
              </div>

              <div class="form-group">
                <input type="password"
                       v-model="loginForm.password"
                       :placeholder="$t('auth.password')"
                       required>
              </div>

              <div class="form-options">
                <label class="checkbox">
                  <input type="checkbox" v-model="loginForm.remember">
                  {{ $t('auth.remember') }}
                </label>

                <a href="#" class="forgot-password">{{ $t('auth.forgot') }}</a>
              </div>

              <button type="submit" class="btn btn-primary btn-block">
                {{ $t('auth.login') }}
              </button>

            </form>

            <!-- Register -->
            <form v-else @submit.prevent="handleRegister" class="auth-form">

              <div class="form-group">
                <input type="text" v-model="registerForm.fullName" :placeholder="$t('auth.fullName')" required>
              </div>

              <div class="form-group">
                <input type="email" v-model="registerForm.email" :placeholder="$t('auth.email')" required>
              </div>

              <div class="form-group">
                <input type="tel" v-model="registerForm.phone" :placeholder="$t('auth.phone')" required>
              </div>

              <div class="form-group">
                <input type="password" v-model="registerForm.password" :placeholder="$t('auth.password')" required>
              </div>

              <div class="form-group">
                <input type="password" v-model="registerForm.confirmPassword" :placeholder="$t('auth.confirmPassword')" required>
              </div>

              <div class="form-options">
                <label class="checkbox">
                  <input type="checkbox" v-model="registerForm.terms">
                  <a href="#" target="_blank">{{ $t('auth.terms') }}</a>
                  {{ $t('auth.agree') }}
                </label>
              </div>

              <button type="submit" class="btn btn-primary btn-block">
                {{ $t('auth.register') }}
              </button>

            </form>

            <div class="auth-divider">
              <span>{{ $t('auth.or') }}</span>
            </div>

            <div class="social-auth">
              <button class="btn-social btn-google">
                <span>🌐</span> {{ $t('auth.loginGoogle') }}
              </button>

              <button class="btn-social btn-linkedin">
                <span>💼</span> {{ $t('auth.loginLinkedIn') }}
              </button>
            </div>

          </div>
        </div>
      </div>

      <!-- Video Modal -->
      <div class="modal" v-if="showVideoModal" @click.self="showVideoModal = false">
        <div class="modal-content modal-video">
          <button class="modal-close" @click="showVideoModal = false">×</button>

          <div class="video-wrapper">
            <iframe width="100%" height="100%"
                    src="https://www.youtube.com/embed/your-video-id"
                    frameborder="0"
                    allowfullscreen>
            </iframe>
          </div>

        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue'
import Navbar from '@/components/Navbar.vue';
import { useAuthStore } from '@/stores/auth'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

export default {
  name: 'LandingPage',
  components: { Navbar },

  setup() {
    const { t } = useI18n()
    const authStore = useAuthStore()
    const router = useRouter()
    //const route = useRoute()

    const isVisible = ref(false)
    const activeService = ref(null)
    const showVideoModal = ref(false)
    const showAuthModal = ref(false)
    const authTab = ref('login')
    const isSubmitting = ref(false)

    const loginUrl = computed(() => {
      const url = new URL('/login', window.location.origin)
      url.searchParams.set('companyId', '5')
      return url.toString()
    })
    const loginQrImageUrl = computed(() =>
      `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=8&data=${encodeURIComponent(loginUrl.value)}`
    )
    const copyLoginUrl = async () => {
      try {
        await navigator.clipboard.writeText(loginUrl.value)
        window.alert('\u0644\u06cc\u0646\u06a9 \u0648\u0631\u0648\u062f \u06a9\u067e\u06cc \u0634\u062f.')
      } catch (error) {
        window.prompt('\u0644\u06cc\u0646\u06a9 \u0648\u0631\u0648\u062f \u0631\u0627 \u06a9\u067e\u06cc \u06a9\u0646\u06cc\u062f:', loginUrl.value)
      }
    }

    const form = reactive({
      name: '',
      phone: '',
      email: '',
      businessType: '',
      message: ''
    })

    const loginForm = reactive({
      email: '',
      password: '',
      remember: false
    })

    const registerForm = reactive({
      fullName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      terms: false
    })

    const stats = ref([
      { id: 1, number: '۳۰+', labelKey: 'landing.stats.experience' },
      { id: 2, number: '۵۰۰+', labelKey: 'landing.stats.activeCustomers' },
      { id: 3, number: '۴', labelKey: 'landing.stats.softwareGenerations' },
      { id: 4, number: '۱۰۰٪', labelKey: 'landing.stats.satisfaction' }
    ])

    const timeline = ref([
      { 
        year: '۱۳۷۰',
        icon: '💾',
        titleKey: 'landing.timeline.dos.title',
        descriptionKey: 'landing.timeline.dos.description'
      },
      { 
        year: '۱۳۸۰',
        icon: '🪟',
        titleKey: 'landing.timeline.windows.title',
        descriptionKey: 'landing.timeline.windows.description'
      },
      { 
        year: '۱۳۹۰',
        icon: '🌐',
        titleKey: 'landing.timeline.web.title',
        descriptionKey: 'landing.timeline.web.description'
      },
      { 
        year: '۱۴۰۰',
        icon: '🤖',
        titleKey: 'landing.timeline.ai.title',
        descriptionKey: 'landing.timeline.ai.description'
      }
    ])

    const services = ref([
      {
        id: 1,
        icon: '🏪',
        titleKey: 'landing.services.store.title',
        descriptionKey: 'landing.services.store.description',
        featureKeys: [
          'landing.services.store.features.pos',
          'landing.services.store.features.inventory',
          'landing.services.store.features.reports',
          'landing.services.store.features.support'
        ]
      },
      {
        id: 2,
        icon: '🏢',
        titleKey: 'landing.services.company.title',
        descriptionKey: 'landing.services.company.description',
        featureKeys: [
          'landing.services.company.features.accounting',
          'landing.services.company.features.storage',
          'landing.services.company.features.payroll',
          'landing.services.company.features.managementReports'
        ]
      },
      {
        id: 3,
        icon: '🏭',
        titleKey: 'landing.services.factory.title',
        descriptionKey: 'landing.services.factory.description',
        featureKeys: [
          'landing.services.factory.features.production',
          'landing.services.factory.features.qualityControl',
          'landing.services.factory.features.supplyChain',
          'landing.services.factory.features.costAnalysis'
        ]
      },
      {
        id: 4,
        icon: '🧠',
        titleKey: 'landing.services.ai.title',
        descriptionKey: 'landing.services.ai.description',
        featureKeys: [
          'landing.services.ai.features.profitForecast',
          'landing.services.ai.features.patternDetect',
          'landing.services.ai.features.smartAlert',
          'landing.services.ai.features.riskAnalysis'
        ]
      }
    ])

    const aiFeatures = ref([
      { id: 1, icon: '🔍', titleKey: 'landing.ai.pattern', descriptionKey: 'landing.ai.patternDesc' },
      { id: 2, icon: '⚠️', titleKey: 'landing.ai.warning', descriptionKey: 'landing.ai.warningDesc' },
      { id: 3, icon: '📈', titleKey: 'landing.ai.profit', descriptionKey: 'landing.ai.profitDesc' },
      { id: 4, icon: '🎯', titleKey: 'landing.ai.optimize', descriptionKey: 'landing.ai.optimizeDesc' }
    ])

    const steps = ref([
      { number: '۱', titleKey: 'landing.steps.consult', descriptionKey: 'landing.steps.consultDesc' },
      { number: '۲', titleKey: 'landing.steps.demo', descriptionKey: 'landing.steps.demoDesc' },
      { number: '۳', titleKey: 'landing.steps.implementation', descriptionKey: 'landing.steps.implementationDesc' },
      { number: '۴', titleKey: 'landing.steps.training', descriptionKey: 'landing.steps.trainingDesc' },
      { number: '۵', titleKey: 'landing.steps.support', descriptionKey: 'landing.steps.supportDesc' }
    ])

    onMounted(() => {
      //if (authStore?.isAuthenticated) router.push('/dashboard')
      isVisible.value = true
    })

    const openAuthModal = (tab) => {
      if (tab === 'login') router.push({ path: '/login', query: loginUrl.value ? Object.fromEntries(new URL(loginUrl.value).searchParams) : {} })
      else if (tab === 'register') router.push('/register')
    }

    const closeAuthModal = () => {
      showAuthModal.value = false
    }

    const handleLogin = async () => {
      try {
        await authStore.login(loginForm)
        closeAuthModal()
      } catch (error) {
        console.error('Login failed:', error)
      }
    }

    const handleRegister = async () => {
      if (registerForm.password !== registerForm.confirmPassword) {
        alert(t('auth.errors.passwordMismatch'))
        return
      }
      if (!registerForm.terms) {
        alert(t('auth.errors.acceptTerms'))
        return
      }
      try {
        await authStore.register(registerForm)
        closeAuthModal()
      } catch (error) {
        console.error('Registration failed:', error)
      }
    }

    const scrollToServices = () => {
      document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })
    }

    const showServiceDetails = (service) => {
      console.log('Service details:', service)
    }

    const submitForm = async () => {
      isSubmitting.value = true
      await new Promise(resolve => setTimeout(resolve, 2000))
      isSubmitting.value = false

      form.name = ''
      form.phone = ''
      form.email = ''
      form.businessType = ''
      form.message = ''

      alert(t('form.success.submit'))
    }

    const goBack = () => {
      router.push('/select-role')
    }

    return {
      authStore,
      isVisible,
      activeService,
      showVideoModal,
      showAuthModal,
      authTab,
      isSubmitting,
      loginUrl,
      loginQrImageUrl,
      copyLoginUrl,
      form,
      loginForm,
      registerForm,
      stats,
      timeline,
      services,
      aiFeatures,
      steps,
      goBack,
      openAuthModal,
      closeAuthModal,
      handleLogin,
      handleRegister,
      scrollToServices,
      showServiceDetails,
      submitForm,
      t
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* =========================
   Base
   ========================= */

.landing-page {
  background: var(--bg);
}
body {
  direction: rtl;
  background: var(--bg);
  color: var(--text);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Animation Classes */
.animate-in {
  opacity: 0;
  transform: translateY(20px);
  animation: fadeInUp 0.6s ease forwards;
}

@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Top Banner */
.top-banner {
  background: linear-gradient(135deg, var(--top-banner-1), var(--top-banner-2));
  color: white;
  padding: 0.75rem 0;
  position: relative;
  overflow: hidden;
}

.about-section {
  background-color: var(--bg);
}

.services-section {
  background-color: var(--bg);
}


.top-banner::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    45deg,
    transparent 30%,
    rgba(255, 255, 255, 0.1) 50%,
    transparent 70%
  );
  animation: shimmer 3s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.banner-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  position: relative;
  z-index: 10;
}

.banner-text {
  font-size: 1rem;
  font-weight: 500;
}

.banner-buttons {
  display: flex;
  gap: 0.5rem;
}

.btn-banner {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s;
}

.btn-register {
  background: var(--orange-1);
  color: white;
}

.btn-register:hover {
  background: var(--orange-2);
  transform: translateY(-2px);
}

.btn-login {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.btn-login:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Header */
.header {
  background: linear-gradient(135deg, var(--header-1) 0%, var(--header-2) 100%);
  color: white;
  position: relative;
  overflow: hidden;
}

.header::before {
  content: '';
  position: absolute;
  width: 200%;
  height: 200%;
  background: radial-gradient(
    circle,
    rgba(59, 130, 246, 0.1) 0%,
    transparent 50%
  );
  animation: rotate 30s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Hero Section */
.hero {
  padding: 2rem 0 6rem;
  position: relative;
  z-index: 20;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  /* keep look; adapt opacity for dark using gradient tokens */
  background: linear-gradient(
    to left,
    rgba(15, 23, 42, 0.9),
    rgba(30, 41, 59, 0.8)
  );
}

.hero-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  position: relative;
  z-index: 30;
}

.hero-content {
  max-width: 600px;
}

.hero-badge {
  margin-bottom: 2rem;
}

.badge-pulse {
  background: linear-gradient(135deg, var(--gradient-1), var(--gradient-2));
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  display: inline-block;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
    transform: scale(0.95);
  }
}

.hero-title {
  font-size: 3.5rem;
  line-height: 1.2;
  margin-bottom: 1.5rem;
}

.gradient-text {
  background: linear-gradient(135deg, #60a5fa, #c084fc);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  position: relative;
}

.hero-subtitle {
  font-size: 1.25rem;
  opacity: 0.9;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.hero-buttons {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 2rem;
}

.khane-login-share {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  width: min(340px, 100%);
  margin: 0 0 2rem;
  padding: 1.25rem;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  overflow-wrap: anywhere;
}

.khane-login-share p { margin: 0.4rem 0 0; font-size: 0.9rem; line-height: 1.6; }
.khane-login-qr { display: block; width: 190px; height: 190px; padding: 8px; border-radius: 12px; background: #fff; }
.khane-login-link { color: white; text-decoration: underline; }

/* Trust Badges */
.trust-badges {
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
}

.trust-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--muted-2);
}

.trust-icon {
  font-size: 1rem;
}

/* Hero Image */
.hero-image-wrapper {
  position: relative;
}

.hero-image {
  position: relative;
  z-index: 30;
}

.dashboard-preview {
  width: 100%;
  opacity: 0;
  transform: translateY(20px);
  transition: all 1s ease;
  filter: drop-shadow(0 20px 30px var(--shadow-3));
}

.dashboard-preview.animated {
  opacity: 1;
  transform: translateY(0);
}

.floating-cta-card {
  position: absolute;
  bottom: -2rem;
  right: -2rem;
  background: var(--card-bg);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 25px 50px -12px var(--shadow-3);
  animation: float 3s ease-in-out infinite;
  z-index: 40;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.cta-card-content {
  text-align: center;
}

.cta-card-content h4 {
  color: var(--heading);
  font-size: 1.25rem;
  margin-bottom: 0.25rem;
}

.cta-card-content p {
  color: var(--muted);
  margin-bottom: 1rem;
}

/* Stats Section */
.stats-section {
  padding: 4rem 0;
  background: var(--card-bg);
  position: relative;
  z-index: 50;
  margin-top: -2rem;
  border-radius: 2rem 2rem 0 0;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
}

.stat-card {
  text-align: center;
  padding: 2rem;
  background: var(--bg);
  border-radius: 16px;
  transition: all 0.3s;
  border: 1px solid var(--card-border);
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 25px -5px var(--shadow-1);
  border-color: var(--gradient-1);
}

.stat-number {
  font-size: 3rem;
  font-weight: bold;
  background: linear-gradient(135deg, #1e40af, var(--gradient-1));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.5rem;
}

.stat-label {
  color: #475569;
  font-weight: 500;
}

html[data-theme="dark"] .stat-label {
  color: rgba(226, 232, 240, 0.75);
}

/* Section Headers */
.section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--heading), var(--header-1));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1rem;
}

.section-subtitle {
  font-size: 1.125rem;
  color: var(--muted);
}

/* Timeline */
.timeline {
  position: relative;
  margin-top: 4rem;
  
}

.timeline::before {
  content: '';
  position: absolute;
  right: 50%;
  transform: translateX(50%);
  width: 2px;
  height: 100%;
  background: linear-gradient(to bottom, var(--gradient-1), var(--gradient-2));
}

.timeline-item {
  display: flex;
  align-items: center;
  margin-bottom: 3rem;
  position: relative;
}

.timeline-item:nth-child(even) {
  flex-direction: row-reverse;
}

.timeline-year {
  background: linear-gradient(135deg, var(--gradient-1), var(--gradient-2));
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: bold;
  min-width: 80px;
  text-align: center;
  box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.3);
}

.timeline-content {
  flex: 1;
  padding: 2rem;
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 10px 15px -3px var(--shadow-1);
  margin: 0 2rem;
  border: 1px solid var(--card-border);
  color: var(--text);
}

.timeline-icon {
  font-size: 2rem;
  background: var(--card-bg);
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 15px -3px var(--shadow-1);
  border: 2px solid var(--gradient-1);
}

/* Services Grid */
.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
}

.service-card {
  background: var(--card-bg);
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 10px 15px -3px var(--shadow-1);
  transition: all 0.3s;
  position: relative;
  overflow: hidden;
  border: 1px solid var(--card-border);
}

.service-card::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(to left, var(--gradient-1), var(--gradient-2));
  transform: scaleX(0);
  transition: transform 0.3s;
}

.service-card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 25px 30px -12px var(--shadow-2);
}

.service-card:hover::before {
  transform: scaleX(1);
}

.service-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  transition: transform 0.3s;
}

.service-icon.active {
  transform: scale(1.1) rotate(5deg);
}

.service-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--heading);
  margin-bottom: 0.5rem;
}

.service-description {
  color: var(--muted);
  margin-bottom: 1rem;
  line-height: 1.6;
}

.service-features {
  list-style: none;
  margin-bottom: 1.5rem;
}

.service-features li {
  color: #475569;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

html[data-theme="dark"] .service-features li {
  color: rgba(226, 232, 240, 0.78);
}

.feature-check {
  color: var(--green);
  font-weight: bold;
}

.service-footer {
  margin-top: auto;
}

/* AI Features */
.ai-features {
  background: linear-gradient(
    135deg,
    var(--bg),
    var(--card-border)
  );
  padding: 6rem 0;
  position: relative;
  overflow: hidden;
}

.ai-features::before {
  content: '';
  position: absolute;
  width: 300px;
  height: 300px;
  background: radial-gradient(
    circle,
    rgba(59, 130, 246, 0.1) 0%,
    transparent 70%
  );
  top: -150px;
  left: -150px;
  border-radius: 50%;
}

.ai-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
}

.ai-card {
  background: var(--card-bg);
  padding: 2rem;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 10px 15px -3px var(--shadow-1);
  transition: all 0.3s;
  border: 1px solid var(--card-border);
  backdrop-filter: blur(10px);
}

.ai-card:hover {
  transform: translateY(-4px) scale(1.05);
  box-shadow: 0 25px 30px -12px rgba(59, 130, 246, 0.3);
  border-color: var(--gradient-1);
}

.ai-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  display: inline-block;
  padding: 1rem;
  background: linear-gradient(135deg, #e0f2fe, #dbeafe);
  border-radius: 60% 40% 50% 50% / 40% 50% 50% 60%;
  animation: morph 8s ease-in-out infinite;
}

html[data-theme="dark"] .ai-icon {
  background: linear-gradient(135deg, rgba(96, 165, 250, 0.25), rgba(168, 85, 247, 0.25));
}

@keyframes morph {
  0%,
  100% {
    border-radius: 60% 40% 50% 50% / 40% 50% 50% 60%;
  }
  25% {
    border-radius: 40% 60% 50% 50% / 40% 60% 50% 50%;
  }
  50% {
    border-radius: 50% 50% 40% 60% / 50% 50% 40% 60%;
  }
  75% {
    border-radius: 50% 50% 60% 40% / 60% 40% 50% 50%;
  }
}

/* Journey Section */
.journey-section {
  padding: 6rem 0;
  background: var(--card-bg);
}

.journey-steps {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 3rem;
}

.step {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 1.5rem;
  position: relative;
  border: 1px solid var(--card-border);
  transition: all 0.3s;
  overflow: hidden;
}

.step:hover {
  transform: translateX(-10px);
  box-shadow: 0 15px 20px -10px var(--shadow-1);
  border-color: var(--gradient-1);
}

.step-number {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, var(--gradient-1), var(--gradient-2));
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
  position: relative;
  z-index: 10;
}

.step-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 4px;
  background: linear-gradient(to left, var(--gradient-1), var(--gradient-2));
  transition: width 0.3s;
}

.step-content h3 {
  color: var(--heading);
  margin-bottom: 0.5rem;
  font-size: 1.25rem;
}

.step-content p {
  color: var(--muted);
  line-height: 1.6;
}

/* CTA Section */
.cta-section {
  padding: 6rem 0;
  background: linear-gradient(135deg, var(--header-1), var(--header-2));
  color: white;
  position: relative;
  overflow: hidden;
}

.cta-wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
}

.cta-content {
  position: relative;
  z-index: 20;
}

.cta-title {
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
}

.cta-description {
  font-size: 1.125rem;
  opacity: 0.9;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.cta-features {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.cta-feature {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.cta-feature-icon {
  background: var(--green);
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
}

.cta-buttons {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.btn-outline-light {
  background: transparent;
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
}

.btn-outline-light:hover {
  background: white;
  color: var(--header-1);
}

.cta-image {
  position: relative;
  z-index: 20;
}

.cta-image img {
  width: 100%;
  animation: float 6s ease-in-out infinite;
}

/* Contact Section */
.contact-section {
  padding: 6rem 0;
  background: var(--bg);
}

.contact-wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  background: var(--card-bg);
  padding: 3rem;
  border-radius: 32px;
  box-shadow: 0 25px 30px -12px var(--shadow-1);
  border: 1px solid var(--card-border);
}

.contact-info {
  padding: 2rem;
}

.contact-description {
  color: var(--muted);
  font-size: 1.125rem;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.contact-details {
  margin-top: 2rem;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: var(--bg);
  border-radius: 12px;
  transition: all 0.3s;
}

.contact-item:hover {
  transform: translateX(-5px);
  background: rgba(241, 245, 249, 0.7);
}

html[data-theme="dark"] .contact-item:hover {
  background: rgba(15, 23, 42, 0.55);
}

.contact-icon {
  font-size: 1.5rem;
  background: linear-gradient(135deg, #dbeafe, #ede9fe);
  width: 60px;
  height: 60px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

html[data-theme="dark"] .contact-icon {
  background: linear-gradient(
    135deg,
    rgba(99, 102, 241, 0.25),
    rgba(168, 85, 247, 0.25)
  );
}

.contact-item h4 {
  color: var(--heading);
  margin-bottom: 0.25rem;
}

.contact-item p {
  color: var(--muted);
}

.social-proof {
  margin-top: 3rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, var(--bg), rgba(241, 245, 249, 0.9));
  border-radius: 16px;
  text-align: center;
}

html[data-theme="dark"] .social-proof {
  background: linear-gradient(135deg, rgba(11, 18, 32, 1), rgba(15, 23, 42, 0.7));
}

.highlight-number {
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--gradient-1);
}

.company-logos {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin-top: 1rem;
}

.company-placeholder {
  font-size: 2rem;
  opacity: 0.7;
  transition: all 0.3s;
}

.company-placeholder:hover {
  opacity: 1;
  transform: scale(1.1);
}

.contact-form-wrapper {
  padding: 2rem;
}

.contact-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 1rem;
  border: 2px solid var(--card-border);
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s;
  background: var(--bg);
  color: var(--text);
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--focus-border);
  background: var(--card-bg);
  box-shadow: 0 0 0 4px var(--ring);
}

/* Footer */
.footer {
  background: var(--header-1);
  color: white;
  padding: 4rem 0 2rem;
}

.footer-content {
  display: grid;
  grid-template-columns: 2fr 2fr 1fr;
  gap: 3rem;
  margin-bottom: 3rem;
}

.footer-brand {
  max-width: 300px;
}

.footer-tagline {
  opacity: 0.8;
  margin-top: 1rem;
  line-height: 1.6;
  color: #cbd5e1;
}

.footer-newsletter {
  margin-top: 2rem;
}

.footer-newsletter h4 {
  margin-bottom: 1rem;
  color: #f1f5f9;
}

.newsletter-form {
  display: flex;
  gap: 0.5rem;
}

.newsletter-form input {
  flex: 1;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid #334155;
  background: var(--header-2);
  color: white;
}

.newsletter-form input::placeholder {
  color: #94a3b8;
}

.btn-subscribe {
  padding: 0.75rem 1.5rem;
  background: var(--gradient-1);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-subscribe:hover {
  background: #2563eb;
}

.footer-links {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}

.link-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.link-group h4 {
  color: #f1f5f9;
  margin-bottom: 0.5rem;
  font-size: 1.125rem;
}

.link-group a {
  color: #cbd5e1;
  text-decoration: none;
  transition: all 0.3s;
  font-size: 0.9375rem;
}

.link-group a:hover {
  color: #93c5fd;
  transform: translateX(-5px);
}

.footer-social {
  text-align: center;
}

.footer-social h4 {
  margin-bottom: 1rem;
  color: #f1f5f9;
}

.social-icons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 2rem;
}

.social-icon {
  width: 45px;
  height: 45px;
  background: var(--header-2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: white;
  transition: all 0.3s;
  font-size: 1.25rem;
}

.social-icon:hover {
  background: var(--gradient-1);
  transform: translateY(-3px) scale(1.1);
}

.app-badges {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.app-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: var(--header-2);
  border-radius: 8px;
  color: white;
  text-decoration: none;
  transition: all 0.3s;
}

.app-badge:hover {
  background: #334155;
  transform: translateY(-2px);
}

.footer-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 2rem;
  border-top: 1px solid #334155;
  opacity: 0.8;
  font-size: 0.875rem;
  color: #94a3b8;
}

.footer-bottom-links {
  display: flex;
  gap: 2rem;
}

.footer-bottom-links a {
  color: #94a3b8;
  text-decoration: none;
  transition: color 0.3s;
}

.footer-bottom-links a:hover {
  color: #cbd5e1;
}

/* Buttons */
.btn {
  padding: 0.875rem 1.75rem;
  border-radius: 12px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 1rem;
  position: relative;
  overflow: hidden;
}

.btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.btn:hover::before {
  width: 300px;
  height: 300px;
}

.btn-primary {
  background: linear-gradient(135deg, var(--gradient-1), #2563eb);
  color: white;
  box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.3);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(59, 130, 246, 0.4);
}

.btn-secondary {
  background: #f1f5f9;
  color: var(--text);
  border: 1px solid var(--card-border);
}

html[data-theme="dark"] .btn-secondary {
  background: rgba(226, 232, 240, 0.08);
}

.btn-secondary:hover {
  background: #e2e8f0;
  transform: translateY(-2px);
}

html[data-theme="dark"] .btn-secondary:hover {
  background: rgba(226, 232, 240, 0.14);
}

.btn-outline {
  background: transparent;
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
}

.btn-outline:hover {
  background: white;
  color: var(--header-1);
}

.btn-large {
  padding: 1rem 2rem;
  font-size: 1.1rem;
}

.btn-small {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
}

.btn-block {
  width: 100%;
}

/* Auth Modal */
.modal {
  position: fixed;
  inset: 0;
  background: var(--modal-overlay);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: var(--card-bg);
  border-radius: 32px;
  padding: 2rem;
  max-width: 450px;
  width: 100%;
  position: relative;
  animation: modalSlide 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes modalSlide {
  from {
    opacity: 0;
    transform: translateY(-50px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-close {
  position: absolute;
  left: 1rem;
  top: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--muted);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.modal-close:hover {
  background: rgba(241, 245, 249, 1);
  transform: rotate(90deg);
}

html[data-theme="dark"] .modal-close:hover {
  background: rgba(226, 232, 240, 0.08);
}

.auth-tabs {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  border-bottom: 2px solid var(--card-border);
  padding-bottom: 1rem;
}

.auth-tab {
  flex: 1;
  padding: 0.75rem;
  background: none;
  border: none;
  font-size: 1rem;
  font-weight: 600;
  color: var(--muted-2);
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
}

.auth-tab::after {
  content: '';
  position: absolute;
  bottom: -1rem;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(135deg, var(--gradient-1), var(--gradient-2));
  transform: scaleX(0);
  transition: transform 0.3s;
}

.auth-tab.active {
  color: var(--heading);
}

.auth-tab.active::after {
  transform: scaleX(1);
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.875rem;
}

.checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #475569;
}

html[data-theme="dark"] .checkbox {
  color: rgba(226, 232, 240, 0.78);
}

.forgot-password {
  color: var(--gradient-1);
  text-decoration: none;
}

.forgot-password:hover {
  text-decoration: underline;
}

.auth-divider {
  text-align: center;
  margin: 1.5rem 0;
  position: relative;
}

.auth-divider::before,
.auth-divider::after {
  content: '';
  position: absolute;
  top: 50%;
  width: 45%;
  height: 1px;
  background: var(--card-border);
}

.auth-divider::before {
  left: 0;
}

.auth-divider::after {
  right: 0;
}

.auth-divider span {
  background: var(--card-bg);
  padding: 0 1rem;
  color: var(--muted-2);
  font-size: 0.875rem;
}

.social-auth {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.btn-social {
  padding: 0.875rem;
  border: 2px solid var(--card-border);
  border-radius: 12px;
  background: var(--card-bg);
  color: var(--text);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-social:hover {
  background: rgba(241, 245, 249, 1);
  border-color: #94a3b8;
}

html[data-theme="dark"] .btn-social:hover {
  background: rgba(226, 232, 240, 0.10);
}

.modal-video {
  max-width: 800px;
  padding: 0;
  overflow: hidden;
}

.video-wrapper {
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
}

.video-wrapper iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* Spinner */
.spinner {
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
  display: inline-block;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive */
@media (max-width: 1024px) {
  .hero-title {
    font-size: 3rem;
  }

  .hero-grid {
    gap: 2rem;
  }

  .footer-content {
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
  }

  .footer-brand {
    grid-column: span 2;
    max-width: none;
  }
}

@media (max-width: 768px) {
  .hero-grid {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .hero-buttons {
    justify-content: center;
  }

  .trust-badges {
    justify-content: center;
  }

  .hero-title {
    font-size: 2.5rem;
  }

  .contact-wrapper {
    grid-template-columns: 1fr;
  }

  .cta-wrapper {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .cta-features {
    justify-content: center;
  }

  .cta-buttons {
    justify-content: center;
  }

  .footer-content {
    grid-template-columns: 1fr;
  }

  .footer-brand {
    grid-column: span 1;
  }

  .footer-links {
    grid-template-columns: 1fr 1fr;
  }

  .footer-bottom {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }

  .timeline::before {
    right: 20px;
  }

  .timeline-item,
  .timeline-item:nth-child(even) {
    flex-direction: column;
    align-items: flex-start;
  }

  .timeline-content {
    margin: 1rem 0 0 0;
  }

  .banner-content {
    flex-direction: column;
    text-align: center;
  }

  .banner-buttons {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 2rem;
  }

  .hero-buttons {
    flex-direction: column;
    width: 100%;
  }

  .btn-large {
    width: 100%;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .services-grid {
    grid-template-columns: 1fr;
  }

  .ai-grid {
    grid-template-columns: 1fr;
  }

  .contact-wrapper {
    padding: 1rem;
  }

  .footer-links {
    grid-template-columns: 1fr;
  }

  .floating-cta-card {
    display: none;
  }

  .modal-content {
    padding: 1.5rem;
  }

  .form-options {
    flex-direction: column;
    gap: 0.5rem;
  }
}
</style>
